import {
	IPropertyHolder, IProperty, IPropertyAdjustmentCondition, IPropertyAdjustment, IAdjustmentChanges, IComponentType, EComponentTypes, IFilterPropertyDictionary,
	IDropdownOptionDictionary, ErrorHelper, QueryBuilderHelper, IControlType, EPropertyTypes, EPropertyAdjustmentConditionOperator, ITabEnabledCondition, EControlTypes, EModalType
} from './';
import { VALIDATION_UNSAVED_CONTROL_ERROR, VALIDATION_UNSAVED_LOOP_ERROR } from '../constants';
import { clone } from './clone';
import store from '../store/store';
import types from '../store/types';
import { QueryBuilder } from '../components/index';

export class PropertyHelper {

	private static defaultedProperties: {
		controlTypeName: string,
		propertyName: string
	}[] = [
			{ controlTypeName: 'Google Map', propertyName: 'Minimum Zoom Level' },
			{ controlTypeName: 'Google Map', propertyName: 'Default Zoom Level' },
			{ controlTypeName: 'Google Map', propertyName: 'Default Zoom Level - User Location' },
			{ controlTypeName: 'ESRI Map', propertyName: 'Minimum Zoom Level' },
			{ controlTypeName: 'ESRI Map', propertyName: 'Default Zoom Level' },
			{ controlTypeName: 'ESRI Map', propertyName: 'Default Zoom Level - User Location' }
		]; //TODO: implement default properties using a more flexible system

	public static isDefaultedProperty(controlType: IControlType, propertyName: string) {
		return PropertyHelper.defaultedProperties.some(p => p.controlTypeName === controlType.NameType && p.propertyName === propertyName);
	}

	// Update a specific property on a Control or Component
	public static updateProperty = <T extends IPropertyHolder>(object: T, Name: string, Value: any): T => {

		let cloneObject: T = clone(object);
		let prop: IProperty = cloneObject.Properties.filter(p => p.Name === Name)[0];
		if (!prop && Name === "Hyperlink Column")
			prop = cloneObject.Properties.filter(p => p.Name === "Column")[0];
		prop.Value = Value;

		return cloneObject;

	}

	public static getProperty(properties: IProperty[], propertyName: string): IProperty {

		if (properties) {
			let props: IProperty[] = properties.filter(p => p.Name === propertyName);
			if (props && props[0]) {
				return props[0];
			}
		}

		return null;
	}

	public static getPropertyValue(properties: IProperty[], propertyName: string): any {
		if (properties) {
			let props: IProperty[] = properties.filter(p => p.Name === propertyName);
			if (props && props[0]) {
				if (props[0].Value !== null && props[0].Value !== undefined) {
					return props[0].Value;
				}
				else {
					return props[0].Default;
				}
			}
		}

		return null;
	}

	public static getErrorMessages = (properties: IProperty[], verifyValueOptions: boolean, verifyJSONValueOptions?: boolean): string[] => {

		//loop through and validate each property
		let errorMessages = properties.filter((v) => {
			//keep required properties, numbers, and queries

			return (v.IsRequired && !v.IsInvisible) || v.Type === EPropertyTypes.number || v.Type === EPropertyTypes.decimal || v.Type === EPropertyTypes.query ||
				v.Type === EPropertyTypes.validationConfig;
		}).map((v) => {

			let adjustmentChanges = PropertyHelper.GetAdjustmentChanges(v, v.Value, properties);

			if (adjustmentChanges.IsVisible === false) {
				return '';
			}

			let label: string = adjustmentChanges.Label || v.Name;

			//build the error message
			let errorMessage: string = '';
			switch (v.Type) {
				case EPropertyTypes.number:
					errorMessage = (v.Value === '' && !v.IsRequired) || !isNaN(parseFloat(v.Value)) && isFinite(v.Value) ? '' : 'A numeric value must be supplied for ' + label;
					break;
				case EPropertyTypes.decimal:
					errorMessage = (v.Value === '' && !v.IsRequired) || !isNaN(parseFloat(v.Value)) && isFinite(v.Value) ? '' : 'A valid decimal value must be supplied for ' + label;
					break;
				case EPropertyTypes.input:
				case EPropertyTypes.dropdown:
					errorMessage = v.Value && v.Value.length ? '' : 'An option must be selected for ' + label;
					break;
				case EPropertyTypes.dynamic:
					let hasValue = true;
					if (!v.Value) {
						hasValue = false;
						errorMessage = hasValue ? '' : 'A value must be supplied for ' + label;
					}
					else {
						let text = JSON.parse(v.Value).text;
						hasValue = text && text.length;
						errorMessage = hasValue ? '' : 'A value must be supplied for ' + label;
					}
					break;
				case EPropertyTypes.query:
					let canSaveQueryEditor: boolean = QueryBuilderHelper.canSaveQueryEditor(v.Id);
					if (!canSaveQueryEditor) {
						let advancedQueryIsValidating: boolean = QueryBuilderHelper.advancedQueryIsValidating(v.Id);
						if (advancedQueryIsValidating) {
							errorMessage = 'Please wait until the Advanced Mode expression for ' + label + ' has completed validation';
						}
						else {
							errorMessage = 'A valid query must be defined for ' + label;
						}
					}
					else {
						errorMessage = '';
					}
					break;
				case EPropertyTypes.validationConfig:
					errorMessage = '';
					let canSaveValidationConfig: boolean = QueryBuilderHelper.canSaveQueryEditor(v.Id);
					let modalState = store.getState().modalState;
					let editedValidationItem = modalState.controlConfig.editedValidationItem || modalState.loopConfigState.loopConfig.editedValidationItem;
					if (!canSaveValidationConfig || editedValidationItem) {
						switch (modalState.modal.openModal) {

							case EModalType.ControlConfig:
								errorMessage = VALIDATION_UNSAVED_CONTROL_ERROR;
								break;
							case EModalType.LoopConfig:
								errorMessage = VALIDATION_UNSAVED_LOOP_ERROR;
								break;

						}
					}
					break;
				default:
					errorMessage = (v.Value !== null && typeof v.Value !== 'undefined' && v.Value !== "") ? '' : 'A value must be supplied for ' + label;
					break;
			};

			return errorMessage;
		}).filter((v) => {
			return v.length; //filter out empty error messages
		});

		if (verifyValueOptions) {

			let errorMessage: string = '';

			if (verifyJSONValueOptions) {
				let jsonOptions: IProperty = properties.filter(prop => prop.Name === 'Json Options')[0];
				try {
					let value = JSON.parse(jsonOptions.Value).text;
					if (value.length === 0) {
						errorMessage = 'Options must be specified either in the Values tab or Json Options field';
					}
				} catch (e) {
					errorMessage = 'Options must be specified either in the Values tab or Json Options field';
				}
			}
			else {
				//there must be at least one option specified for List Of Options control properties
				let options = properties.filter(prop => prop.Name === 'Value Options')[0];
				errorMessage = options && options.ValueOptions && !options.ValueOptions.length ?
					'Options must be specified either in the Values tab or Json Options field' : '';

				if (errorMessage.length) {
					errorMessages.push(errorMessage);
				}

				if (options && options.ValueOptions && options.ValueOptions.length) {
					//if any option has an imageUrl, then make sure it is either blank or a valid url
					let invalidImageUrlErrorMessages: string[] = options.ValueOptions.filter(v => {
						return v.imageUrl && v.imageUrl.length && !PropertyHelper.isValidUrl(v.imageUrl);
					}).map(v => 'Values Option ' + v.text + ' is not using a valid Image URL');

					errorMessages = errorMessages.concat(invalidImageUrlErrorMessages);
				}
			}

		}

		return errorMessages;
	}

	public static isValidUrl = (url: string) => {

		let regexp = /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/;
		if (regexp.test(url)) {
			return true;
		}
		else {
			return false;
		}
	}

	public static getDefaultProperties = (type: IComponentType, templates: any): IProperty[] => {

		let properties: IProperty[];

		switch (type.ComponentTypeId) {

			case EComponentTypes.action:
				properties = clone(templates[type.ComponentTypeId].Action.Properties);
				break;

			case EComponentTypes.block:
				properties = clone(templates[type.ComponentTypeId].ControlBlock.Properties);
				break;

			case EComponentTypes.step:
				properties = clone(templates[type.ComponentTypeId].Step.Properties);
				break;

			case EComponentTypes.decision:
				properties = clone(templates[type.ComponentTypeId].Decision.Properties);
				break;

			case EComponentTypes.sharedJourney:
				properties = clone(templates[type.ComponentTypeId].SharedJourney.Properties);
				break;

			case EComponentTypes.submission:
				properties = clone(templates[type.ComponentTypeId].Submission.Properties);
				break;

			case EComponentTypes.summary:
				properties = clone(templates[type.ComponentTypeId].Summary.Properties);
				break;

			case EComponentTypes.redirect:
				properties = clone(templates[type.ComponentTypeId].Redirect.Properties);
				break;

		}

		return properties;
	}

	public static updatePropertyFilterState(properties: IProperty[]) {
		properties.forEach(p => {
			if (p.FilterControls && p.FilterFields) {
				store.dispatch({
					type: types.ADD_PROPERTY_FILTER_STATE,
					propertyId: p.Id,
					filterControls: p.FilterControls,
					filterFields: p.FilterFields
				});
			}
		});
	}

	public static clearPropertyFilterState() {
		store.dispatch({
			type: types.CLEAR_PROPERTY_FILTER_STATE
		});
	}

	public static resolveAdjustedFields(properties: IProperty[], propertyFilters: IFilterPropertyDictionary, key: string, value: any, resetChildValue?: boolean) {

		let parentPropertyId: string = properties.some(p => p.Name === key) ? properties.filter(p => p.Name === key)[0].Id : null;
		if (parentPropertyId) {
			//determine if the affected property is involved in any property filters
			let childProperties: IProperty[] = properties.filter(p => p.PropertiesAdjustments !== null);
			//Determine which filter method the property uses
			if (childProperties && childProperties.length) {
				for (let i = 0; i < childProperties.length; i++) {
					for (let j = 0; j < childProperties[i].PropertiesAdjustments.length; j++) {
						let affectedProperty: boolean = false;
						for (let k = 0; k < childProperties[i].PropertiesAdjustments[j].PropertyAdjustmentConditionList.length; k++) {
							//Is this property supposed to be adjusted?
							if (parentPropertyId === childProperties[i].PropertiesAdjustments[j].PropertyAdjustmentConditionList[k].PropertyId) {
								affectedProperty = true;
							}
						}
						if (affectedProperty) {
							for (let k = 0; k < childProperties[i].PropertiesAdjustments[j].PropertyAdjustmentList.length; k++) {
								let adjustmentType: any = childProperties[i].PropertiesAdjustments[j].PropertyAdjustmentList[k].AdjustmentType.Arguments["Remove Items"];
								if (adjustmentType && adjustmentType.length) {
									for (let l = 0; l < adjustmentType.length; l++) {
										//Is the child property value supposed to excluded?
										if (childProperties[i].Value === adjustmentType[l]) {
											childProperties[i].Value = '';
										}
									}
								}
							}
						}
					}
				}
			}
			// If the affected property uses the old filter method.
			else {
				if (!(propertyFilters && propertyFilters.length))
					return;
				for (let childPropertyId in propertyFilters) {
					let filter = propertyFilters[childPropertyId];
					filter.FilterControls.forEach(fc => {
						if (fc.FilterControlId === parentPropertyId) {
							//we have found a child property that will be affected
							let childProperty: IProperty = properties.some(p => p.Id === childPropertyId) ? properties.filter(p => p.Id === childPropertyId)[0] : null;
							if (childProperty) {
								let filterPropertyName = fc.FieldToFilter;
								let validChildPropertyValues: any[] = filter.FilterFields.filter(ff => ff.fields[filterPropertyName] === value).map(v => v[fc.FilterControlFieldName]);
								if (resetChildValue) {
									//ensure that the value of child property is valid given the parent property value
									let childValueIsValid = validChildPropertyValues.some(v => v === childProperty.Value);
									if (!childValueIsValid) {
										childProperty.Value = '';
									}
								}

							}
						}
					});
				}
			}
		}
	}

	public static getFilteredValueOptions(properties: IProperty[], propertyFilters: IFilterPropertyDictionary): IDropdownOptionDictionary {

		let filteredValueOptions: IDropdownOptionDictionary = {};

		if (!propertyFilters) {
			return null;
		}

		properties.forEach(childProperty => {
			if (childProperty.ValueOptions && propertyFilters[childProperty.Id] && propertyFilters[childProperty.Id].FilterControls && propertyFilters[childProperty.Id].FilterFields) {

				let filter = propertyFilters[childProperty.Id];
				filteredValueOptions[childProperty.Id] = childProperty.ValueOptions.filter(vo => {

					//get the subset of filter fields that relate to vo
					let filterFields = filter.FilterFields.filter(ff => ff.key === vo.key);

					//ensure that the conditions are met for at least one of these filter fields
					return filterFields.some(ff => {
						let validFilterField = true;

						for (let fieldName in ff.fields) {
							let parentPropertyId: string = filter.FilterControls.filter(c => c.FieldToFilter === fieldName)[0].FilterControlId;
							let parentProperty = properties.filter(p => p.Id === parentPropertyId)[0];
							if (parentProperty.Value === null || parentProperty.Value !== ff.fields[fieldName]) {
								return false;
							}
						}
						return validFilterField;
					});

				});

			}
		});

		return filteredValueOptions;
	}

	public static GetAdjustmentChanges = (property: IProperty, value: any, properties: IProperty[]): IAdjustmentChanges => {

		let adjustmentChanges: IAdjustmentChanges = {
			Label: null,
			IsVisible: true,
			OptionValues: null,
			Value: null
		};

		if (property.PropertiesAdjustments) {
			property.PropertiesAdjustments.forEach(paConditions => {

				let meetAdjustmentCondition: boolean = PropertyHelper.PropertiesAdjustmentsCheckConditions(
					paConditions.PropertyAdjustmentConditionList, properties);

				if (meetAdjustmentCondition) {
					adjustmentChanges = PropertyHelper.PerformPropertyAdjustments(adjustmentChanges, paConditions.PropertyAdjustmentList, property);

					if (adjustmentChanges.Value !== null && adjustmentChanges.Value !== undefined && adjustmentChanges.Value !== value) {
						property.Value = adjustmentChanges.Value;
					}
				}
			});
		}

		return adjustmentChanges;
	}

	public static PerformPropertyAdjustments = (adjustmentChanges: IAdjustmentChanges,
		propertiesAdjustments: IPropertyAdjustment[], property: IProperty): IAdjustmentChanges => {

		propertiesAdjustments.forEach(propertyAdjustment => {

			switch (propertyAdjustment.Type.toString()) {
				case "Hide Property":
					adjustmentChanges.IsVisible = false;
					if (property.Value && property.Value !== property.Default && property.Name != "Required") {
						property.Value = (property.Default) ? property.Default : null;
					}
					break;
				case "Label Change":
					adjustmentChanges.Label = propertyAdjustment.AdjustmentType.Arguments["New Label"] as string;
					break;
				case "Value Change":
					//Might need checking later, dynamic vlaues dont quite work the best here. 
					adjustmentChanges.Value = propertyAdjustment.AdjustmentType.Arguments["Value"] as string;
					break;
				case "Dropdown Remove Values":
					if (property.ValueOptions !== null) {
						adjustmentChanges.OptionValues = [];
						adjustmentChanges.OptionValues = adjustmentChanges.OptionValues.concat(property.ValueOptions);
						let listOfOptionsToRemove: string[] = propertyAdjustment.AdjustmentType.Arguments["Remove Items"] as string[];
						adjustmentChanges.OptionValues = adjustmentChanges.OptionValues.filter(d =>
							listOfOptionsToRemove.indexOf(d.text) === -1
						);
					}
					break;
			}
		});
		return adjustmentChanges;
	}
	public static PropertiesAdjustmentsCheckConditions = (propertyAdjustmentCondition: IPropertyAdjustmentCondition[]
		, propertiesList: IProperty[],): boolean => {
		let meetAdjustmentCondition: boolean = false;

		propertyAdjustmentCondition.forEach(u => {
			let getProp = propertiesList.filter(p => p.Id === u.PropertyId)[0];
			let propValue = getProp.Value;
			if (getProp.Type === EPropertyTypes.dynamic && getProp.Value) {
				let jsonValue = JSON.parse(getProp.Value);
				propValue = jsonValue.text;
			}

			// getProp
			if (getProp !== null) {
				switch (u.Operator) {
					case EPropertyAdjustmentConditionOperator.Equals:
						meetAdjustmentCondition = propValue == u.Value;
						break;
					case EPropertyAdjustmentConditionOperator.NotEquals:
						meetAdjustmentCondition = propValue != u.Value;
						break;
					case EPropertyAdjustmentConditionOperator.GreaterThan:
						meetAdjustmentCondition = propValue > u.Value;
						break;
					case EPropertyAdjustmentConditionOperator.IsEmpty:
						meetAdjustmentCondition = !(propValue && propValue.length);
						break;
					case EPropertyAdjustmentConditionOperator.IsNotEmpty:
						meetAdjustmentCondition = !!(propValue && propValue.length);
						break;
				}
			}
		});
		return meetAdjustmentCondition;
	}

	public static TabEnabledConditionsMet(tabName: string, tabEnabledConditions: ITabEnabledCondition[], properties: IProperty[]): string[] {

		if (!(tabEnabledConditions && tabEnabledConditions.length)) {
			return [];
		}

		let relevantConditions: ITabEnabledCondition[] = tabEnabledConditions
			.filter(c => c.TabName === tabName)
			;

		if (!relevantConditions.length) {
			return [];
		}

		let warningMessages: string[] = relevantConditions
			.map(c => PropertyHelper.PropertiesAdjustmentsCheckConditions([c.Condition], properties) ? null : c.WarningMessage)
			.filter(m => !!m);

		return warningMessages;
	}

	/**
	 * Extracts attribute names from XML within a JSON object and prefixes them with their link-entity alias
	 * @param {string|object} input - Either a JSON string or object containing an XML string in a "text" property
	 * @returns {Array} Array of strings in the format "alias.attributeName"
	 */
	public static extractLinkEntityAttributes(input: any) {
		let xmlString= input;

		// Create a DOM parser
		const parser = new DOMParser();
		const xmlDoc = parser.parseFromString(xmlString, "text/xml");

		// Array to store results
		const results = [];

		// Find all link-entity elements
		const linkEntities = xmlDoc.getElementsByTagName("link-entity");

		// Process each link-entity
		for (let i = 0; i < linkEntities.length; i++) {
			const linkEntity = linkEntities[i];

			// Get the alias of the link-entity
			const alias = linkEntity.getAttribute("alias");

			if (alias) {
				// Find all attribute elements within this link-entity
				const attributeElements = linkEntity.getElementsByTagName("attribute");

				// For each attribute, create the prefixed format
				for (let j = 0; j < attributeElements.length; j++) {
					const attributeName = attributeElements[j].getAttribute("name");
					if (attributeName) {
						results.push(`${alias}.${attributeName}`);
					}
				}
			}
		}

		return results;
	}

}


// WEBPACK FOOTER //
// ./source/ts/helpers/property.ts